import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";

const FilterModal = ({
  onApplyFilter,
  handleClose,
  currentFilter,
  handleClearFilters,
}) => {
  const [activeType, setActiveType] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [height, setHeight] = useState(window.innerHeight);

  const filterGroups = useMemo(() => {
    return [
      {
        key: "mode",
        title: "Mode",
        prefix: "MODE_",
        options: [
          { label: "UPI", value: "2" },
          { label: "Credit Card", value: "4" },
        ],
      },
    ];
  }, []);

  useEffect(() => {
    setActiveType("mode");
  }, []);

  useEffect(() => {
    if (currentFilter?.value) {
      const filterValue = currentFilter.value;

      for (const group of filterGroups) {
        if (filterValue.startsWith(group.prefix)) {
          const rawValue = filterValue.replace(group.prefix, "");
          const match = group.options.find((o) => o.value === rawValue);

          if (match) {
            setActiveType(group.key);

            setSelectedFilter({
              type: group.title,
              value: match.value,
              label: match.label,
            });
            break;
          }
        }
      }
    } else {
      setSelectedFilter(null);
    }
  }, [currentFilter, filterGroups]);

  useEffect(() => {
    const resize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const handleApply = useCallback(() => {
    if (!selectedFilter) {
      toast.error("Please select at least one filter before applying.");
      return;
    }

    const group = filterGroups.find((g) => g.title === selectedFilter.type);
    const prefix = group?.prefix || "";

    onApplyFilter(
      prefix + selectedFilter.value,
      selectedFilter.label,
      selectedFilter.type,
    );
  }, [selectedFilter, filterGroups, onApplyFilter]);

  const handleTypeClick = useCallback((key) => {
    setActiveType(key);
  }, []);

  const handleOptionClick = useCallback((option, groupTitle) => {
    setSelectedFilter({
      type: groupTitle,
      value: option.value,
      label: option.label,
    });
  }, []);

  const handleClearClick = useCallback(() => {
    setSelectedFilter(null);
    setActiveType("mode");
    handleClearFilters();
  }, [handleClearFilters]);

  const activeGroup =
    filterGroups.find((g) => g.key === activeType) || filterGroups[0];

  return (
    <Container style={{ height }}>
      {/* Header */}
      <Header>
        <h1 className="text-lg font-medium text-white">Filters</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      {/* Body */}
      <Body>
        {/* LEFT – Filter Types */}
        <LeftPane>
          {filterGroups.map((group) => {
            const active = activeType === group.key;
            return (
              <TypeItem
                key={group.key}
                active={active}
                onClick={() => handleTypeClick(group.key)}
              >
                {group.title}
              </TypeItem>
            );
          })}
        </LeftPane>

        {/* RIGHT – Options */}
        <RightPane>
          <FilterTitle>{activeGroup.title}</FilterTitle>

          {activeGroup.options.map((option) => {
            const isCurrentType = selectedFilter?.type === activeGroup.title;
            const checked =
              isCurrentType && selectedFilter?.value === option.value;

            return (
              <OptionRow
                key={option.value}
                onClick={() => handleOptionClick(option, activeGroup.title)}
              >
                <input
                  type="radio"
                  checked={checked}
                  readOnly
                  style={{ margin: 0, width: 16 }}
                />
                <span className="leading-tight text-sm text-gray-600">
                  {option.label}
                </span>
              </OptionRow>
            );
          })}
        </RightPane>
      </Body>

      {/* Footer */}
      <ActionSection>
        <SideCancelBtn type="button" onClick={handleClearClick}>
          Clear
        </SideCancelBtn>
        <SubmitBtn type="submit" onClick={handleApply}>
          Apply Filter
        </SubmitBtn>
      </ActionSection>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-primary-color px-4 py-4`;

const Body = tw.div`flex flex-1 overflow-hidden`;

const LeftPane = tw.div`
  w-1/3 bg-gray-50 border-r overflow-y-auto`;

const TypeItem = tw.div`
  px-4 py-4 cursor-pointer text-md
  ${({ active }) =>
    active
      ? "bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color"
      : "text-gray-700 hover:bg-gray-100"}
`;

const RightPane = tw.div`
  flex-1
  py-4
  pl-4
  pr-6
  overflow-y-auto
`;
export const ActionSection = tw.div`px-4 py-2 bg-white flex justify-between border-t border-gray-200 absolute bottom-0 left-0 w-full`;
export const SideCancelBtn = tw.button`
  text-sm  w-32  md:w-44 h-12 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed   md:text-base  text-gray-600 `;

export const SubmitBtn = tw.button`
text-sm w-32  md:w-44 h-12 grid place-items-center disabled:opacity-50 disabled:cursor-not-allowed  md:text-base hover:bg-hover-color text-white rounded-md shadow-md bg-primary-color `;
// const SubmitBtn = tw.button`${(p) =>
//   p.$active
//     ? "bg-gray-100"
//     : ""} border whitespace-nowrap border-gray-200 flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6  cursor-pointer bg-primary-color hover:bg-hover-color  py-2.5 text-sm`;
const OptionRow = tw.div`
  flex items-center gap-3
  py-3 px-2
  cursor-pointer
  text-sm text-gray-400
  rounded-md
  hover:bg-gray-50
`;

const FilterTitle = tw.h3`text-md font-medium text-gray-700 mb-2`;

export default FilterModal;
